// /lifecycle — public roadmap page that explains the three ways MowKind
// keeps a yard cared for. Linked from Home, About, dashboard cycle-end
// notices, and admin AdminNav.

import { Link } from 'react-router-dom';

export function LifecyclePage() {
  return (
    <div className="max-w-4xl mx-auto px-6 lg:px-12 py-16">
      <div className="text-xs tracking-[0.25em] uppercase text-rose mb-3">How MowKind keeps a yard cared for</div>
      <h1
        className="text-5xl text-forest mb-3 leading-tight font-display"
        style={{ fontVariationSettings: '"opsz" 144' }}
      >
        Three ways. One mission.
      </h1>
      <p className="text-forest/75 mb-12 max-w-2xl leading-relaxed">
        Every yard MowKind cares for moves through up to three modes of
        support. We start free, and we never leave a household stranded
        when the free window ends.
      </p>

      {/* STAGE 1 */}
      <section className="mb-12">
        <div className="flex items-baseline gap-4 mb-3">
          <div className="text-rose font-display text-4xl" style={{ fontVariationSettings: '"opsz" 144' }}>1</div>
          <h2 className="text-2xl text-forest font-display">The Free Cycle</h2>
        </div>
        <p className="text-forest/80 leading-relaxed mb-3">
          A neighbor in need applies. We review the application personally
          and confirm by phone within five business days. Approved
          households get three months of free service.
        </p>
        <p className="text-forest/80 leading-relaxed mb-3">
          They're matched with a <strong>Pro Mowkin</strong> (paid by the
          program) or queued for a <strong>monthly volunteer mow day</strong> —
          the first Saturday of each month, neighbors gather and clear a
          route together.
        </p>
        <div className="flex flex-wrap gap-3 mt-4">
          <Link to="/apply" className="text-xs tracking-[0.15em] uppercase border border-rose text-rose px-4 py-2 hover:bg-rose hover:text-cream transition-colors">
            Apply for a free mow
          </Link>
          <Link to="/volunteer" className="text-xs tracking-[0.15em] uppercase border border-forest/30 text-forest px-4 py-2 hover:bg-forest hover:text-cream transition-colors">
            Volunteer
          </Link>
        </div>
      </section>

      {/* STAGE 2 */}
      <section className="mb-12">
        <div className="flex items-baseline gap-4 mb-3">
          <div className="text-rose font-display text-4xl" style={{ fontVariationSettings: '"opsz" 144' }}>2</div>
          <h2 className="text-2xl text-forest font-display">Neighborhood Continuation</h2>
        </div>
        <p className="text-forest/80 leading-relaxed mb-3">
          When the free cycle ends, the household can opt in to have
          their neighborhood pool funds for another cycle. The Mowkin who
          had been taking care of the yard <strong>proposes a fair price</strong>.
          We review the bid. Once approved, the listing goes public and
          neighbors pledge what they can.
        </p>
        <p className="text-forest/80 leading-relaxed mb-3">
          The resident's full name and house number stay private — neighbors
          see only "a household on E. Pearl St." so they can recognize a
          place they know without anyone being exposed.
        </p>
        <p className="text-forest/80 leading-relaxed">
          When pledges meet the bid, the Mowkin gets paid for another cycle
          and the relationship continues.
        </p>
        <div className="flex flex-wrap gap-3 mt-4">
          <Link to="/keep-mowing" className="text-xs tracking-[0.15em] uppercase border border-rose text-rose px-4 py-2 hover:bg-rose hover:text-cream transition-colors">
            See households needing continuation
          </Link>
        </div>
      </section>

      {/* STAGE 3 */}
      <section className="mb-12">
        <div className="flex items-baseline gap-4 mb-3">
          <div className="text-rose font-display text-4xl" style={{ fontVariationSettings: '"opsz" 144' }}>3</div>
          <h2 className="text-2xl text-forest font-display">Neighbor Commitment</h2>
        </div>
        <p className="text-forest/80 leading-relaxed mb-3">
          The other path: a <strong>vetted volunteer in the same
          neighborhood</strong> signs up to mow that yard themselves on a
          recurring cadence — weekly, every other week, monthly, or whatever
          they choose.
        </p>
        <p className="text-forest/80 leading-relaxed mb-3">
          No money changes hands. It's purely neighbor-to-neighbor care.
          We confirm the commitment with both the volunteer and the household,
          then it shows up on the volunteer's dashboard like any other mow.
        </p>
        <p className="text-forest/80 leading-relaxed">
          For volunteers, this is the most direct way to know exactly whose
          life you're affecting and to build a real relationship over time.
        </p>
        <div className="flex flex-wrap gap-3 mt-4">
          <Link to="/keep-mowing" className="text-xs tracking-[0.15em] uppercase border border-rose text-rose px-4 py-2 hover:bg-rose hover:text-cream transition-colors">
            Commit to a household
          </Link>
        </div>
      </section>

      {/* FOOTER NOTE */}
      <div className="border-t border-forest/15 pt-8 mt-12">
        <div className="text-xs tracking-[0.2em] uppercase text-forest/55 mb-3">A note on privacy</div>
        <p className="text-forest/75 text-sm leading-relaxed">
          A household never gets listed publicly without opting in. Even
          then, identifying details are kept off the public page. Only
          vetted volunteers ever see a household's exact address.
        </p>
      </div>
    </div>
  );
}
